import { createSlice } from "@reduxjs/toolkit"

// Initial state
const initialState = {
  theme: "light",
  sidebarOpen: true,
  sidebarCollapsed: false,
  notifications: [],
  modals: {
    createProject: false,
    editProject: false,
    createWork: false,
    editWork: false,
    createObservation: false,
    editObservation: false,
    uploadFiles: false,
    confirmDelete: false,
  },
  loading: {
    global: false,
    projects: false,
    works: false,
    observations: false,
    files: false,
  },
  alerts: [],
  breadcrumbs: [],
  activeTab: "overview",
  viewMode: "grid", // 'grid' or 'list'
  pageSize: 10,
  sortBy: "createdAt",
  sortOrder: "desc",
}

// UI slice
const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    // Theme actions
    setTheme: (state, action) => {
      state.theme = action.payload
      localStorage.setItem("theme", action.payload)
    },
    toggleTheme: (state) => {
      state.theme = state.theme === "light" ? "dark" : "light"
      localStorage.setItem("theme", state.theme)
    },

    // Sidebar actions
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarCollapsed: (state, action) => {
      state.sidebarCollapsed = action.payload
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },

    // Notification actions
    addNotification: (state, action) => {
      const notification = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      }
      state.notifications.unshift(notification)

      // Keep only the latest 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50)
      }
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter((notification) => notification.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
    markNotificationAsRead: (state, action) => {
      const notification = state.notifications.find((n) => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.read = true
      })
    },

    // Modal actions
    openModal: (state, action) => {
      const modalName = action.payload
      if (state.modals.hasOwnProperty(modalName)) {
        state.modals[modalName] = true
      }
    },
    closeModal: (state, action) => {
      const modalName = action.payload
      if (state.modals.hasOwnProperty(modalName)) {
        state.modals[modalName] = false
      }
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((modalName) => {
        state.modals[modalName] = false
      })
    },

    // Loading actions
    setLoading: (state, action) => {
      const { key, value } = action.payload
      if (state.loading.hasOwnProperty(key)) {
        state.loading[key] = value
      }
    },
    setGlobalLoading: (state, action) => {
      state.loading.global = action.payload
    },

    // Alert actions
    addAlert: (state, action) => {
      const alert = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      }
      state.alerts.push(alert)
    },
    removeAlert: (state, action) => {
      state.alerts = state.alerts.filter((alert) => alert.id !== action.payload)
    },
    clearAlerts: (state) => {
      state.alerts = []
    },

    // Breadcrumb actions
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload
    },
    addBreadcrumb: (state, action) => {
      state.breadcrumbs.push(action.payload)
    },
    clearBreadcrumbs: (state) => {
      state.breadcrumbs = []
    },

    // Tab actions
    setActiveTab: (state, action) => {
      state.activeTab = action.payload
    },

    // View mode actions
    setViewMode: (state, action) => {
      state.viewMode = action.payload
      localStorage.setItem("viewMode", action.payload)
    },
    toggleViewMode: (state) => {
      state.viewMode = state.viewMode === "grid" ? "list" : "grid"
      localStorage.setItem("viewMode", state.viewMode)
    },

    // Pagination and sorting actions
    setPageSize: (state, action) => {
      state.pageSize = action.payload
      localStorage.setItem("pageSize", action.payload.toString())
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload
    },
    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === "asc" ? "desc" : "asc"
    },

    // Reset UI state
    resetUIState: (state) => {
      return {
        ...initialState,
        theme: state.theme, // Preserve theme
        viewMode: state.viewMode, // Preserve view mode
        pageSize: state.pageSize, // Preserve page size
      }
    },
  },
})

export const {
  setTheme,
  toggleTheme,
  setSidebarOpen,
  toggleSidebar,
  setSidebarCollapsed,
  toggleSidebarCollapsed,
  addNotification,
  removeNotification,
  clearNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  openModal,
  closeModal,
  closeAllModals,
  setLoading,
  setGlobalLoading,
  addAlert,
  removeAlert,
  clearAlerts,
  setBreadcrumbs,
  addBreadcrumb,
  clearBreadcrumbs,
  setActiveTab,
  setViewMode,
  toggleViewMode,
  setPageSize,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  resetUIState,
} = uiSlice.actions

export default uiSlice.reducer
